/* ═══════════════════════════════════════════════════════════════
   Tasks — Kanban board + List view with task detail, subtasks, comments
   ═══════════════════════════════════════════════════════════════ */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import type {
  Task,
  TaskAssignment,
  TaskComment,
  TaskNotification,
  TaskLink,
  TaskAttachment,
  TaskProjectLink,
  TaskStatus,
  TaskPriority,
  TeamMember,
  Project,
  Team,
  TeamAssignment as TeamAssign,
} from '../../shared/types';
import { TaskService } from '../services/taskService';
import { TeamService } from '../services/teamService';
import { ProjectService } from '../services/projectService';
import { DataTable, Column, GroupOption } from '../components/DataTable';
import { SearchableSelect } from '../components/SearchableSelect';
import { listCached } from '../services/cloudCache';
import { hasPermission } from '../../shared/permissions';
import { Button } from '../components/ui/button';
import { Plus, X, Check, List, ListOrdered } from 'lucide-react';

interface TasksPageProps {
  toast: (msg: string, isError?: boolean) => void;
  authUser: any;
}

type ViewMode = 'board' | 'list';

const STATUS_COLS: { id: TaskStatus; label: string; color: string }[] = [
  { id: 'todo', label: 'To Do', color: 'var(--tm)' },
  { id: 'in_progress', label: 'In Progress', color: 'var(--ac)' },
  { id: 'review', label: 'Review', color: 'var(--color-warning)' },
  { id: 'done', label: 'Done', color: 'var(--ok)' },
];

const PRIORITY_COLORS: Record<TaskPriority, string> = {
  low: 'var(--tm)',
  medium: 'var(--ac)',
  high: 'var(--color-warning)',
  urgent: 'var(--dg)',
};

const PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

function generateId(): string {
  return 'task-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 6);
}

// ─── Rich Text Editor (contenteditable with toolbar) ───

function RichTextEditor({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}) {
  const editorRef = React.useRef<HTMLDivElement>(null);
  const [isEmpty, setIsEmpty] = useState(!value || value === '<br>');

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
      setIsEmpty(!value || value === '<br>');
    }
  }, []);

  function handleInput() {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    setIsEmpty(!html || html === '<br>' || html === '<div><br></div>');
    onChange(html);
  }

  function exec(cmd: string, val?: string) {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    handleInput();
  }

  function insertLink() {
    const url = prompt('Enter URL:');
    if (url) exec('createLink', url);
  }

  const tbtn: React.CSSProperties = {
    padding: '3px 7px',
    borderRadius: 3,
    border: 'none',
    background: 'transparent',
    color: 'var(--tm)',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1,
  };

  return (
    <div
      style={{
        border: '1px solid var(--bd)',
        borderRadius: 5,
        overflow: 'hidden',
        background: 'var(--ib)',
      }}
    >
      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          gap: 1,
          padding: '3px 4px',
          borderBottom: '1px solid var(--bd)',
          background: 'var(--sa)',
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          onClick={() => exec('bold')}
          style={{ ...tbtn, fontWeight: 700 }}
          title="Bold"
          aria-label="Bold"
        >
          B
        </button>
        <button
          type="button"
          onClick={() => exec('italic')}
          style={{ ...tbtn, fontStyle: 'italic' }}
          title="Italic"
          aria-label="Italic"
        >
          I
        </button>
        <button
          type="button"
          onClick={() => exec('underline')}
          style={{ ...tbtn, textDecoration: 'underline' }}
          title="Underline"
          aria-label="Underline"
        >
          U
        </button>
        <span style={{ width: 1, background: 'var(--bd)', margin: '2px 3px' }} />
        <button
          type="button"
          onClick={() => exec('insertUnorderedList')}
          style={{ ...tbtn, display: 'inline-flex', alignItems: 'center' }}
          title="Bullet list"
          aria-label="Bullet list"
        >
          <List size={13} />
        </button>
        <button
          type="button"
          onClick={() => exec('insertOrderedList')}
          style={{ ...tbtn, display: 'inline-flex', alignItems: 'center' }}
          title="Numbered list"
          aria-label="Numbered list"
        >
          <ListOrdered size={13} />
        </button>
        <span style={{ width: 1, background: 'var(--bd)', margin: '2px 3px' }} />
        <button
          type="button"
          onClick={insertLink}
          style={{ ...tbtn, textDecoration: 'underline', color: 'var(--ac)' }}
          title="Insert link"
        >
          Link
        </button>
        <button
          type="button"
          onClick={() => exec('removeFormat')}
          style={{ ...tbtn, fontSize: 10 }}
          title="Clear formatting"
        >
          Clear
        </button>
      </div>
      {/* Editor */}
      <div style={{ position: 'relative', minHeight: 80 }}>
        {isEmpty && (
          <div
            style={{
              position: 'absolute',
              top: 8,
              left: 10,
              color: 'var(--tm)',
              fontSize: 13,
              pointerEvents: 'none',
              opacity: 0.6,
            }}
          >
            {placeholder || 'Type here...'}
          </div>
        )}
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onBlur={handleInput}
          style={{
            padding: '8px 10px',
            minHeight: 80,
            outline: 'none',
            color: 'var(--tx)',
            fontSize: 13,
            lineHeight: 1.5,
            overflowY: 'auto',
            maxHeight: 300,
          }}
        />
      </div>
    </div>
  );
}

// ─── Searchable Multi-Select Dropdown ───

function SearchableMultiSelect({
  options,
  selected,
  onChange,
  placeholder,
}: {
  options: { value: string; label: string; sub?: string }[];
  selected: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = search
    ? options.filter(
        (o) =>
          o.label.toLowerCase().includes(search.toLowerCase()) ||
          (o.sub || '').toLowerCase().includes(search.toLowerCase())
      )
    : options;

  // Show recently-selected first, then the rest
  const selectedSet = new Set(selected);
  const unselected = filtered.filter((o) => !selectedSet.has(o.value));

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Selected tags */}
      <div
        onClick={() => setOpen(true)}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 4,
          padding: '4px 6px',
          minHeight: 32,
          borderRadius: 5,
          border: '1px solid var(--bd)',
          background: 'var(--ib)',
          cursor: 'text',
          alignItems: 'center',
        }}
      >
        {selected.map((id) => {
          const opt = options.find((o) => o.value === id);
          return (
            <span
              key={id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '1px 6px',
                borderRadius: 10,
                background: 'var(--ac)',
                color: 'var(--color-text-inverse)',
                fontSize: 11,
                fontWeight: 500,
              }}
            >
              {opt?.sub ? `${opt.sub} ` : ''}
              {opt?.label || id}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(selected.filter((s) => s !== id));
                }}
                aria-label={`Remove ${opt?.label || id}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  lineHeight: 1,
                  background: 'transparent',
                  border: 'none',
                  color: 'inherit',
                  padding: 2,
                }}
              >
                <X size={10} />
              </button>
            </span>
          );
        })}
        <input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={selected.length === 0 ? placeholder || 'Search...' : ''}
          style={{
            border: 'none',
            background: 'transparent',
            color: 'var(--tx)',
            fontSize: 13,
            outline: 'none',
            flex: 1,
            minWidth: 60,
            padding: '2px 4px',
          }}
        />
      </div>

      {/* Dropdown */}
      {open && unselected.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 100,
            maxHeight: 200,
            overflowY: 'auto',
            marginTop: 2,
            background: 'var(--sf)',
            border: '1px solid var(--bd)',
            borderRadius: 6,
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          {unselected.slice(0, 20).map((o) => (
            <div
              key={o.value}
              onClick={() => {
                onChange([...selected, o.value]);
                setSearch('');
              }}
              style={{
                padding: '6px 10px',
                fontSize: 13,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sa)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              {o.sub && (
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    color: 'var(--ac)',
                    fontWeight: 700,
                    fontSize: 10,
                    flexShrink: 0,
                  }}
                >
                  {o.sub}
                </span>
              )}
              <span>{o.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ───

function TasksPage({ toast, authUser }: TasksPageProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('board');
  const [tasks, setTasks] = useState<Task[]>(() => listCached<Task>('tasks'));
  const [assignments, setAssignments] = useState<TaskAssignment[]>(() =>
    listCached<TaskAssignment>('task_assignments')
  );
  const [members, setMembers] = useState<TeamMember[]>(() =>
    listCached<TeamMember>('team_members')
  );
  const [projects, setProjects] = useState<Project[]>(() => listCached<Project>('projects'));
  const [teams, setTeams] = useState<Team[]>(() => listCached<Team>('teams'));
  const [teamAssignments, setTeamAssignments] = useState<TeamAssign[]>(() =>
    listCached<TeamAssign>('team_assignments')
  );
  const [taskProjectLinks, setTaskProjectLinks] = useState<TaskProjectLink[]>(() =>
    listCached<TaskProjectLink>('task_project_links')
  );

  // Filters (for PM+ roles)
  const [filterProject, setFilterProject] = useState<string>('');
  const [filterAssignee, setFilterAssignee] = useState<string>('');
  const [filterPriority, setFilterPriority] = useState<string>('');
  const [filterSearch, setFilterSearch] = useState('');

  // Detail modal
  const [detailTaskId, setDetailTaskId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createInStatus, setCreateInStatus] = useState<TaskStatus>('todo');

  const userId = authUser?.teamMemberId || authUser?.id || '';
  // Permission-first. No role fallback — every gate maps to a permission
  // key so custom firm roles can grant/deny each action independently.
  const perms: string[] | undefined = authUser?.permissions;
  const can = (key: string) => hasPermission(perms, key);
  const canCreateTask = can('tasks.create');
  const canEditTask = can('tasks.edit_own') || can('tasks.edit_any');
  const canAssignTeam = can('tasks.assign_team') || can('tasks.assign_anyone');
  const canAssignAnyone = can('tasks.assign_anyone');
  const canArchiveTask = can('tasks.archive');
  const canDeleteTask = can('tasks.delete');
  const canAssign = canAssignTeam;
  const canSeeAll = can('tasks.view_all');
  const canFilter = canAssign;

  // ─── Load data ───
  const loadData = useCallback(
    async (retry = true) => {
      try {
        const [t, a, m, p, tm, ta, tpl] = await Promise.allSettled([
          TaskService.listTasks(),
          TaskService.listTaskAssignments(),
          TeamService.list(),
          ProjectService.list(),
          TeamService.listTeams(),
          TeamService.listTeamAssignments(),
          TaskService.listTaskProjectLinks(),
        ]);
        if (t.status === 'fulfilled') setTasks(t.value);
        if (a.status === 'fulfilled') setAssignments(a.value);
        if (m.status === 'fulfilled') setMembers(m.value);
        if (p.status === 'fulfilled') setProjects(p.value);
        if (tm.status === 'fulfilled') setTeams(tm.value);
        if (ta.status === 'fulfilled') setTeamAssignments(ta.value);
        if (tpl.status === 'fulfilled') setTaskProjectLinks(tpl.value);

        const failures = [t, a, m, p, tm, ta, tpl].filter(
          (r): r is PromiseRejectedResult => r.status === 'rejected'
        );
        if (failures.length > 0) {
          console.error(
            'TasksPage loadData failed:',
            failures.map((f) => f.reason)
          );
          if (retry) setTimeout(() => loadData(false), 1500);
          else
            toast(
              'Failed to load tasks: ' + (failures[0].reason?.message || 'unknown error'),
              true
            );
        }
      } catch (e: any) {
        console.error('TasksPage loadData failed:', e);
        if (retry) setTimeout(() => loadData(false), 1500);
        else toast('Failed to load tasks: ' + e.message, true);
      }
    },
    [toast]
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Live-update: re-pull whenever cloudCache broadcasts a sync-complete
  useEffect(() => {
    const handler = () => loadData();
    window.addEventListener('specforma-sync-complete', handler);
    return () => window.removeEventListener('specforma-sync-complete', handler);
  }, [loadData]);

  // ─── Visibility filtering ───
  // Members: only their assigned tasks
  // Team leaders: their teams' tasks
  // PMs/Admins: all tasks (with filters)
  const visibleTasks = React.useMemo(() => {
    let filtered = tasks;

    // Role-based visibility
    if (!canSeeAll) {
      // Get team member IDs this user can see
      const visibleMemberIds = new Set<string>([userId]);

      if (can('tasks.view_team')) {
        // Manager: see tasks for team members who report to them or are on their teams
        const myTeamIds = teamAssignments
          .filter((ta) => ta.teamMemberId === userId && ta.teamRole === 'leader')
          .map((ta) => ta.teamId);
        const teamMemberIds = teamAssignments
          .filter((ta) => myTeamIds.includes(ta.teamId))
          .map((ta) => ta.teamMemberId);
        teamMemberIds.forEach((id) => visibleMemberIds.add(id));
        // Also add direct reports
        members.filter((m) => m.reportsTo === userId).forEach((m) => visibleMemberIds.add(m.id));
      }

      // Filter to tasks where at least one assignee is visible, or created by this user
      filtered = filtered.filter((t) => {
        if (t.createdBy === userId) return true;
        const taskAssigns = assignments.filter((a) => a.taskId === t.id);
        return taskAssigns.some((a) => visibleMemberIds.has(a.teamMemberId));
      });
    }

    // User-applied filters
    if (filterProject) {
      filtered = filtered.filter((t) => {
        if (t.projectId === filterProject) return true;
        return taskProjectLinks.some(
          (lnk) => lnk.taskId === t.id && lnk.projectId === filterProject
        );
      });
    }
    if (filterAssignee) {
      const assForMember = assignments
        .filter((a) => a.teamMemberId === filterAssignee)
        .map((a) => a.taskId);
      filtered = filtered.filter((t) => assForMember.includes(t.id));
    }
    if (filterPriority) filtered = filtered.filter((t) => t.priority === filterPriority);
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      filtered = filtered.filter(
        (t) => t.title.toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q)
      );
    }

    return filtered;
  }, [
    tasks,
    assignments,
    members,
    teamAssignments,
    taskProjectLinks,
    userId,
    canSeeAll,
    filterProject,
    filterAssignee,
    filterPriority,
    filterSearch,
    perms,
  ]);

  // Only show top-level tasks (not subtasks) in the main view
  const topLevelTasks = visibleTasks.filter((t) => !t.parentTaskId);

  // ─── Helpers ───
  function getMemberName(id: string): string {
    return members.find((m) => m.id === id)?.name || 'Unknown';
  }
  function getProjectName(id?: string): string {
    if (!id) return '';
    return (
      projects.find((p) => p.id === id)?.name ||
      projects.find((p) => p.id === id)?.projectNumber ||
      ''
    );
  }
  function getProjectIdsForTask(taskId: string): string[] {
    const ids = new Set<string>();
    const task = tasks.find((t) => t.id === taskId);
    if (task?.projectId) ids.add(task.projectId);
    taskProjectLinks
      .filter((lnk) => lnk.taskId === taskId)
      .forEach((lnk) => ids.add(lnk.projectId));
    return Array.from(ids);
  }
  function getAssigneesForTask(taskId: string): TeamMember[] {
    const ids = assignments.filter((a) => a.taskId === taskId).map((a) => a.teamMemberId);
    return members.filter((m) => ids.includes(m.id));
  }
  function getSubtaskCount(taskId: string): number {
    return tasks.filter((t) => t.parentTaskId === taskId).length;
  }
  function getSubtasksDone(taskId: string): number {
    return tasks.filter((t) => t.parentTaskId === taskId && t.status === 'done').length;
  }

  // ─── Status change (drag or click) ───
  async function handleStatusChange(taskId: string, newStatus: TaskStatus) {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;
    const updated = { ...task, status: newStatus };
    await TaskService.saveTask(updated);

    // Notify assignees about status change
    const taskAssigns = assignments.filter((a) => a.taskId === taskId && a.teamMemberId !== userId);
    for (const a of taskAssigns) {
      await TaskService.saveTaskNotification({
        id: '',
        taskId,
        teamMemberId: a.teamMemberId,
        type: 'status_change',
        message: `"${task.title}" moved to ${STATUS_COLS.find((s) => s.id === newStatus)?.label}`,
        read: false,
        createdAt: '',
      });
    }

    await loadData();
  }

  // ─── Task card (shared between board + list) ───
  function TaskCard({ task, compact }: { task: Task; compact?: boolean }) {
    const assignees = getAssigneesForTask(task.id);
    const subtaskCount = getSubtaskCount(task.id);
    const subtasksDone = getSubtasksDone(task.id);
    const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'done';

    return (
      <div
        onClick={() => setDetailTaskId(task.id)}
        draggable
        onDragStart={(e) => e.dataTransfer.setData('taskId', task.id)}
        style={{
          padding: compact ? '8px 12px' : '10px 12px',
          background: 'var(--sf)',
          border: '1px solid var(--bd)',
          borderRadius: 8,
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          transition: 'box-shadow .15s',
          borderLeft: `3px solid ${PRIORITY_COLORS[task.priority]}`,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.boxShadow = 'var(--shadow-md)')}
        onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <span
            style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx)', flex: 1, lineHeight: 1.3 }}
          >
            {task.title}
          </span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              padding: '1px 5px',
              borderRadius: 3,
              background: `color-mix(in srgb, ${PRIORITY_COLORS[task.priority]} 20%, transparent)`,
              color: PRIORITY_COLORS[task.priority],
              flexShrink: 0,
              textTransform: 'uppercase',
            }}
          >
            {PRIORITY_LABELS[task.priority]}
          </span>
        </div>

        {(() => {
          const pIds = getProjectIdsForTask(task.id);
          if (pIds.length === 0) return null;
          return (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {pIds.map((pid) => (
                <span
                  key={pid}
                  style={{
                    fontSize: 11,
                    padding: '1px 6px',
                    borderRadius: 8,
                    background: 'color-mix(in srgb, var(--ac) 15%, transparent)',
                    color: 'var(--tm)',
                    fontWeight: 500,
                  }}
                >
                  {getProjectName(pid)}
                </span>
              ))}
            </div>
          );
        })()}

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {assignees.map((m) => (
            <span
              key={m.id}
              style={{
                fontSize: 10,
                padding: '1px 6px',
                borderRadius: 10,
                background: 'var(--sa)',
                color: 'var(--tm)',
                fontWeight: 500,
              }}
            >
              {m.name
                .split(' ')
                .map((n) => n[0])
                .join('')}
            </span>
          ))}
          {subtaskCount > 0 && (
            <span style={{ fontSize: 10, color: 'var(--tm)' }}>
              {subtasksDone}/{subtaskCount} subtasks
            </span>
          )}
          {task.dueDate && (
            <span
              style={{
                fontSize: 10,
                color: isOverdue ? 'var(--dg)' : 'var(--tm)',
                fontWeight: isOverdue ? 600 : 400,
                marginLeft: 'auto',
              }}
            >
              {new Date(task.dueDate).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })}
            </span>
          )}
        </div>
      </div>
    );
  }

  // ─── Kanban Board ───
  function KanbanBoard() {
    function handleDrop(e: React.DragEvent, status: TaskStatus) {
      e.preventDefault();
      const taskId = e.dataTransfer.getData('taskId');
      if (taskId) handleStatusChange(taskId, status);
    }

    return (
      <div style={{ display: 'flex', gap: 12, flex: 1, overflow: 'auto', padding: '0 0 16px' }}>
        {STATUS_COLS.map((col) => {
          const colTasks = topLevelTasks.filter((t) => t.status === col.id);
          return (
            <div
              key={col.id}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => handleDrop(e, col.id)}
              style={{
                flex: 1,
                minWidth: 240,
                display: 'flex',
                flexDirection: 'column',
                background: 'var(--sa)',
                borderRadius: 10,
                border: '1px solid var(--bd)',
              }}
            >
              <div
                style={{
                  padding: '10px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: '1px solid var(--bd)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: col.color,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--tx)' }}>
                    {col.label}
                  </span>
                  <span style={{ fontSize: 10, color: 'var(--tm)', fontWeight: 600 }}>
                    {colTasks.length}
                  </span>
                </div>
                {canCreateTask && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setCreateInStatus(col.id);
                      setShowCreateModal(true);
                    }}
                    aria-label={`Add task to ${col.label}`}
                    title={`Add task to ${col.label}`}
                  >
                    <Plus size={14} />
                  </Button>
                )}
              </div>
              <div
                style={{
                  padding: 8,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  flex: 1,
                  overflow: 'auto',
                }}
              >
                {colTasks.map((t) => (
                  <TaskCard key={t.id} task={t} />
                ))}
                {colTasks.length === 0 && (
                  <div
                    style={{
                      padding: 16,
                      textAlign: 'center',
                      color: 'var(--tm)',
                      fontSize: 11,
                      opacity: 0.6,
                    }}
                  >
                    Drop tasks here
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ─── List View ───

  const priorityOrder: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 };
  const statusOrder: Record<string, number> = { todo: 0, in_progress: 1, review: 2, done: 3 };

  const listColumns = useMemo(
    (): Column<Task>[] => [
      {
        key: 'status',
        label: 'Status',
        width: 110,
        getValue: (t) => statusOrder[t.status] ?? 0,
        render: (t) => {
          const sc = STATUS_COLS.find((s) => s.id === t.status);
          return (
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: 10,
                background: sc ? `color-mix(in srgb, ${sc.color} 18%, transparent)` : undefined,
                color: sc?.color,
              }}
            >
              {sc?.label}
            </span>
          );
        },
        editable: true,
        editType: 'select',
        editOptions: STATUS_COLS.map((s) => ({ value: s.id, label: s.label })),
      },
      {
        key: 'priority',
        label: 'Priority',
        width: 90,
        getValue: (t) => priorityOrder[t.priority] ?? 2,
        render: (t) => (
          <span style={{ fontSize: 10, fontWeight: 700, color: PRIORITY_COLORS[t.priority] }}>
            {PRIORITY_LABELS[t.priority]}
          </span>
        ),
        editable: true,
        editType: 'select',
        editOptions: Object.entries(PRIORITY_LABELS).map(([v, l]) => ({ value: v, label: l })),
      },
      {
        key: 'title',
        label: 'Title',
        width: 280,
        getValue: (t) => t.title,
        render: (t) => (
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--tx)' }}>{t.title}</span>
        ),
        editable: true,
        editType: 'text',
      },
      {
        key: 'project',
        label: 'Project',
        width: 160,
        sortable: true,
        getValue: (t) => {
          const pIds = getProjectIdsForTask(t.id);
          return pIds
            .map((pid) => getProjectName(pid))
            .filter(Boolean)
            .join(', ');
        },
        render: (t) => {
          const pIds = getProjectIdsForTask(t.id);
          return (
            <span style={{ fontSize: 11, color: 'var(--tm)' }}>
              {pIds
                .map((pid) => getProjectName(pid))
                .filter(Boolean)
                .join(', ')}
            </span>
          );
        },
      },
      {
        key: 'assignees',
        label: 'Assignees',
        width: 130,
        sortable: false,
        render: (t) => {
          const assignees = getAssigneesForTask(t.id);
          return (
            <div style={{ display: 'flex', gap: 4 }}>
              {assignees.map((m) => (
                <span
                  key={m.id}
                  style={{
                    fontSize: 10,
                    padding: '1px 6px',
                    borderRadius: 10,
                    background: 'var(--sa)',
                    color: 'var(--tm)',
                  }}
                >
                  {m.name
                    .split(' ')
                    .map((n) => n[0])
                    .join('')}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        key: 'dueDate',
        label: 'Due',
        width: 100,
        getValue: (t) => t.dueDate || '9999',
        render: (t) => {
          const isOverdue = t.dueDate && new Date(t.dueDate) < new Date() && t.status !== 'done';
          return (
            <span
              style={{
                fontSize: 11,
                color: isOverdue ? 'var(--dg)' : 'var(--tm)',
                fontWeight: isOverdue ? 600 : 400,
              }}
            >
              {t.dueDate
                ? new Date(t.dueDate).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  })
                : ''}
            </span>
          );
        },
        editable: true,
        editType: 'date',
      },
      {
        key: 'subtasks',
        label: 'Subtasks',
        width: 80,
        sortable: false,
        render: (t) => {
          const sc = getSubtaskCount(t.id);
          const sd = getSubtasksDone(t.id);
          return (
            <span style={{ fontSize: 11, color: 'var(--tm)' }}>{sc > 0 ? `${sd}/${sc}` : ''}</span>
          );
        },
      },
    ],
    [tasks, assignments, members, projects, taskProjectLinks]
  );

  const listGroupOptions = useMemo(
    (): GroupOption<Task>[] => [
      {
        key: 'status',
        label: 'Status',
        getValue: (t) => STATUS_COLS.find((s) => s.id === t.status)?.label || t.status,
      },
      {
        key: 'priority',
        label: 'Priority',
        getValue: (t) => PRIORITY_LABELS[t.priority] || t.priority,
      },
      {
        key: 'project',
        label: 'Project',
        getValue: (t) => {
          const pIds = getProjectIdsForTask(t.id);
          return pIds.length > 0
            ? pIds
                .map((pid) => getProjectName(pid))
                .filter(Boolean)
                .join(', ') || 'No Project'
            : 'No Project';
        },
      },
      {
        key: 'assignee',
        label: 'Assignee',
        getValue: (t) => {
          const assignees = getAssigneesForTask(t.id);
          return assignees.length > 0 ? assignees.map((m) => m.name).join(', ') : 'Unassigned';
        },
      },
    ],
    [tasks, assignments, members, projects, taskProjectLinks]
  );

  function ListView() {
    return (
      <div
        style={{
          overflow: 'hidden',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <DataTable<Task>
          columns={listColumns}
          data={topLevelTasks}
          keyField="id"
          onRowClick={(t) => setDetailTaskId(t.id)}
          groupOptions={listGroupOptions}
          searchPlaceholder="Search tasks..."
          initialSort={{ key: 'status', direction: 'asc' }}
          emptyIcon="&#128203;"
          emptyTitle="No Tasks"
          emptyMessage="No tasks found"
          storageKey="tasks-list"
          onCellEdit={async (task, key, value) => {
            await TaskService.saveTask({ ...task, [key]: value });
            await loadData();
          }}
        />
      </div>
    );
  }

  // ─── Create Task Modal ───
  function CreateTaskModal() {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [priority, setPriority] = useState<TaskPriority>('medium');
    const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
    const [dueDate, setDueDate] = useState('');
    const [assigneeIds, setAssigneeIds] = useState<string[]>([]);

    async function handleCreate() {
      if (!title.trim()) {
        toast('Title is required', true);
        return;
      }
      const task: Task = {
        id: generateId(),
        title: title.trim(),
        description: description.trim() || undefined,
        status: createInStatus,
        priority,
        projectId: selectedProjectIds[0] || undefined,
        dueDate: dueDate || undefined,
        createdBy: userId,
        createdAt: '',
        updatedAt: '',
      };
      await TaskService.saveTask(task);

      // Save project links
      for (const pid of selectedProjectIds) {
        await TaskService.saveTaskProjectLink({
          id: '',
          taskId: task.id,
          projectId: pid,
          createdAt: '',
        });
      }

      // Create assignments
      for (const memberId of assigneeIds) {
        await TaskService.saveTaskAssignment({
          id: '',
          taskId: task.id,
          teamMemberId: memberId,
          assignedBy: userId,
          assignedAt: '',
        });
        // Notify assignee
        if (memberId !== userId) {
          await TaskService.saveTaskNotification({
            id: '',
            taskId: task.id,
            teamMemberId: memberId,
            type: 'assigned',
            message: `You were assigned "${task.title}"`,
            read: false,
            createdAt: '',
          });
        }
      }

      toast('Task created');
      setShowCreateModal(false);
      await loadData();
    }

    return (
      <div
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) setShowCreateModal(false);
        }}
        style={overlayStyle}
      >
        <div style={{ ...modalStyle, width: 480 }}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>New task</div>

          <label style={labelStyle}>
            Title *
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={inputStyle}
              placeholder="What needs to be done?"
              autoFocus
            />
          </label>

          <div style={labelStyle}>
            Description
            <RichTextEditor
              value={description}
              onChange={setDescription}
              placeholder="Add details, formatting, links..."
            />
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <label style={{ ...labelStyle, flex: 1 }}>
              Priority
              <SearchableSelect
                value={priority}
                options={[
                  { value: 'low', label: 'Low' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'high', label: 'High' },
                  { value: 'urgent', label: 'Urgent' },
                ]}
                onChange={(v) => setPriority(v as TaskPriority)}
                clearable={false}
                searchPlaceholder="Search…"
                style={inputStyle}
              />
            </label>
            <label style={{ ...labelStyle, flex: 1 }}>
              Due Date
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                style={inputStyle}
              />
            </label>
          </div>

          <div style={labelStyle}>
            Projects (optional)
            <SearchableMultiSelect
              options={projects
                .filter((p) => p.status === 'active')
                .map((p) => ({ value: p.id, label: p.name || 'Untitled', sub: p.projectNumber }))}
              selected={selectedProjectIds}
              onChange={setSelectedProjectIds}
              placeholder="Search projects..."
            />
          </div>

          <div style={labelStyle}>
            Assign To
            <SearchableMultiSelect
              options={members.filter((m) => m.active).map((m) => ({ value: m.id, label: m.name }))}
              selected={assigneeIds}
              onChange={setAssigneeIds}
              placeholder="Search team members..."
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <Button variant="outline" size="sm" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button variant="default" size="sm" onClick={handleCreate}>
              Create task
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Task Detail Modal ───
  function TaskDetailModal() {
    const task = tasks.find((t) => t.id === detailTaskId);
    if (!task) return null;

    return (
      <TaskDetail
        task={task}
        tasks={tasks}
        assignments={assignments}
        members={members}
        projects={projects}
        taskProjectLinks={taskProjectLinks}
        userId={userId}
        canAssign={canAssign}
        onClose={() => setDetailTaskId(null)}
        onRefresh={loadData}
        toast={toast}
        getMemberName={getMemberName}
        getProjectName={getProjectName}
      />
    );
  }

  // ─── Render ───
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: 24, gap: 16 }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Tasks</h2>
          <p style={{ fontSize: 13, color: 'var(--tm)', margin: '2px 0 0' }}>
            {topLevelTasks.length} task{topLevelTasks.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* View toggle */}
          <div style={{ display: 'flex', background: 'var(--sa)', borderRadius: 6, padding: 2 }}>
            <button
              onClick={() => setViewMode('board')}
              style={{
                padding: '5px 12px',
                minHeight: 32,
                borderRadius: 4,
                border: 'none',
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
                background: viewMode === 'board' ? 'var(--sf)' : 'transparent',
                color: viewMode === 'board' ? 'var(--tx)' : 'var(--tm)',
                boxShadow: viewMode === 'board' ? 'var(--shadow-sm)' : 'none',
              }}
            >
              Board
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{
                padding: '5px 12px',
                minHeight: 32,
                borderRadius: 4,
                border: 'none',
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
                background: viewMode === 'list' ? 'var(--sf)' : 'transparent',
                color: viewMode === 'list' ? 'var(--tx)' : 'var(--tm)',
                boxShadow: viewMode === 'list' ? 'var(--shadow-sm)' : 'none',
              }}
            >
              List
            </button>
          </div>
          {canAssign && (
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                setCreateInStatus('todo');
                setShowCreateModal(true);
              }}
            >
              <Plus size={14} />
              New task
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      {canFilter && (
        <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', width: 180 }}>
            <input
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              placeholder="Search tasks..."
              style={{
                ...inputStyle,
                width: '100%',
                padding: '5px 10px',
                paddingRight: filterSearch ? 22 : 10,
                fontSize: 11,
                boxSizing: 'border-box',
              }}
            />
            {filterSearch && (
              <button
                onClick={() => setFilterSearch('')}
                aria-label="Clear search"
                style={{
                  position: 'absolute',
                  right: 2,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: 'var(--tm)',
                  cursor: 'pointer',
                  lineHeight: 1,
                  padding: 6,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <X size={12} />
              </button>
            )}
          </div>
          <div style={{ width: 160 }}>
            <SearchableSelect
              value={filterProject}
              options={projects
                .filter((p) => p.status === 'active')
                .map((p) => ({
                  value: p.id,
                  label: p.projectNumber || p.name || 'Untitled',
                  hint: p.projectNumber ? p.name : undefined,
                }))}
              onChange={(v) => setFilterProject(v)}
              placeholder="All Projects"
              searchPlaceholder="Search projects…"
              style={{ ...inputStyle, padding: '5px 8px', fontSize: 11 }}
            />
          </div>
          <div style={{ width: 140 }}>
            <SearchableSelect
              value={filterAssignee}
              options={members.filter((m) => m.active).map((m) => ({ value: m.id, label: m.name }))}
              onChange={(v) => setFilterAssignee(v)}
              placeholder="All People"
              searchPlaceholder="Search people…"
              style={{ ...inputStyle, padding: '5px 8px', fontSize: 11 }}
            />
          </div>
          <div style={{ width: 130 }}>
            <SearchableSelect
              value={filterPriority}
              options={[
                { value: 'urgent', label: 'Urgent' },
                { value: 'high', label: 'High' },
                { value: 'medium', label: 'Medium' },
                { value: 'low', label: 'Low' },
              ]}
              onChange={(v) => setFilterPriority(v)}
              placeholder="All Priorities"
              searchPlaceholder="Search…"
              style={{ ...inputStyle, padding: '5px 8px', fontSize: 11 }}
            />
          </div>
          {(filterProject || filterAssignee || filterPriority || filterSearch) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setFilterProject('');
                setFilterAssignee('');
                setFilterPriority('');
                setFilterSearch('');
              }}
            >
              Clear
            </Button>
          )}
        </div>
      )}

      {/* Content */}
      {viewMode === 'board' ? <KanbanBoard /> : <ListView />}

      {/* Modals */}
      {showCreateModal && <CreateTaskModal />}
      {detailTaskId && <TaskDetailModal />}
    </div>
  );
}

// ─── Task Detail Component ───

interface TaskDetailProps {
  task: Task;
  tasks: Task[];
  assignments: TaskAssignment[];
  members: TeamMember[];
  projects: Project[];
  taskProjectLinks: TaskProjectLink[];
  userId: string;
  canAssign: boolean;
  onClose: () => void;
  onRefresh: () => Promise<void>;
  toast: (msg: string, isError?: boolean) => void;
  getMemberName: (id: string) => string;
  getProjectName: (id?: string) => string;
}

function TaskDetail({
  task,
  tasks,
  assignments,
  members,
  projects,
  taskProjectLinks,
  userId,
  canAssign,
  onClose,
  onRefresh,
  toast,
  getMemberName,
  getProjectName,
}: TaskDetailProps) {
  const [editTitle, setEditTitle] = useState(task.title);
  const [editDesc, setEditDesc] = useState(task.description || '');
  const [editPriority, setEditPriority] = useState(task.priority);
  const [editStatus, setEditStatus] = useState(task.status);
  const [editDueDate, setEditDueDate] = useState(task.dueDate || '');

  // Multi-project: merge legacy projectId + task_project_links
  const initialProjectIds = React.useMemo(() => {
    const ids = new Set<string>();
    if (task.projectId) ids.add(task.projectId);
    taskProjectLinks
      .filter((lnk) => lnk.taskId === task.id)
      .forEach((lnk) => ids.add(lnk.projectId));
    return Array.from(ids);
  }, [task.id, task.projectId, taskProjectLinks]);
  const [editProjectIds, setEditProjectIds] = useState<string[]>(initialProjectIds);

  const [comments, setComments] = useState<TaskComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [showAddSubtask, setShowAddSubtask] = useState(false);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
  const [taskAssigneeIds, setTaskAssigneeIds] = useState<string[]>(
    assignments.filter((a) => a.taskId === task.id).map((a) => a.teamMemberId)
  );
  const [dirty, setDirty] = useState(false);

  // Links & Attachments
  const [links, setLinks] = useState<TaskLink[]>([]);
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [showAddLink, setShowAddLink] = useState(false);
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newLinkLabel, setNewLinkLabel] = useState('');
  const [showAddAttachment, setShowAddAttachment] = useState(false);

  const subtasks = tasks.filter((t) => t.parentTaskId === task.id);

  useEffect(() => {
    TaskService.listTaskComments(task.id)
      .then(setComments)
      .catch(() => {});
    TaskService.listTaskLinks(task.id)
      .then(setLinks)
      .catch(() => {});
    TaskService.listTaskAttachments(task.id)
      .then(setAttachments)
      .catch(() => {});
  }, [task.id]);

  async function handleSave() {
    const updated: Task = {
      ...task,
      title: editTitle.trim(),
      description: editDesc.trim() || undefined,
      priority: editPriority,
      status: editStatus,
      dueDate: editDueDate || undefined,
      projectId: editProjectIds[0] || undefined, // backward compat: first project
    };
    await TaskService.saveTask(updated);

    // Sync project links
    const currentProjLinks = taskProjectLinks.filter((lnk) => lnk.taskId === task.id);
    const currentProjIds = currentProjLinks.map((lnk) => lnk.projectId);
    const projToAdd = editProjectIds.filter((pid) => !currentProjIds.includes(pid));
    const projToRemove = currentProjLinks.filter((lnk) => !editProjectIds.includes(lnk.projectId));
    for (const pid of projToAdd) {
      await TaskService.saveTaskProjectLink({
        id: '',
        taskId: task.id,
        projectId: pid,
        createdAt: '',
      });
    }
    for (const lnk of projToRemove) {
      await TaskService.deleteTaskProjectLink(lnk.id);
    }

    // Sync assignments
    const currentIds = assignments.filter((a) => a.taskId === task.id).map((a) => a.teamMemberId);
    const toAdd = taskAssigneeIds.filter((id) => !currentIds.includes(id));
    const toRemove = currentIds.filter((id) => !taskAssigneeIds.includes(id));

    for (const memberId of toAdd) {
      await TaskService.saveTaskAssignment({
        id: '',
        taskId: task.id,
        teamMemberId: memberId,
        assignedBy: userId,
        assignedAt: '',
      });
      if (memberId !== userId) {
        await TaskService.saveTaskNotification({
          id: '',
          taskId: task.id,
          teamMemberId: memberId,
          type: 'assigned',
          message: `You were assigned "${task.title}"`,
          read: false,
          createdAt: '',
        });
      }
    }
    for (const memberId of toRemove) {
      const ass = assignments.find((a) => a.taskId === task.id && a.teamMemberId === memberId);
      if (ass) await TaskService.deleteTaskAssignment(ass.id);
    }

    // Notify if status changed
    if (editStatus !== task.status) {
      const taskAssigns = assignments.filter(
        (a) => a.taskId === task.id && a.teamMemberId !== userId
      );
      for (const a of taskAssigns) {
        await TaskService.saveTaskNotification({
          id: '',
          taskId: task.id,
          teamMemberId: a.teamMemberId,
          type: 'status_change',
          message: `"${task.title}" moved to ${STATUS_COLS.find((s) => s.id === editStatus)?.label}`,
          read: false,
          createdAt: '',
        });
      }
    }

    toast('Task updated');
    setDirty(false);
    await onRefresh();
  }

  async function handleAddComment() {
    if (!newComment.trim()) return;
    await TaskService.saveTaskComment({
      id: '',
      taskId: task.id,
      authorId: userId,
      text: newComment.trim(),
      createdAt: '',
      updatedAt: '',
    });

    // Notify other assignees and creator
    const notifyIds = new Set<string>();
    assignments.filter((a) => a.taskId === task.id).forEach((a) => notifyIds.add(a.teamMemberId));
    notifyIds.add(task.createdBy);
    notifyIds.delete(userId);
    for (const memberId of notifyIds) {
      await TaskService.saveTaskNotification({
        id: '',
        taskId: task.id,
        teamMemberId: memberId,
        type: 'comment',
        message: `New comment on "${task.title}"`,
        read: false,
        createdAt: '',
      });
    }

    setNewComment('');
    const updated = await TaskService.listTaskComments(task.id);
    setComments(updated);
    toast('Comment added');
  }

  async function handleAddSubtask() {
    if (!newSubtaskTitle.trim()) return;
    const sub: Task = {
      id: generateId(),
      title: newSubtaskTitle.trim(),
      status: 'todo',
      priority: task.priority,
      parentTaskId: task.id,
      projectId: editProjectIds[0] || undefined,
      createdBy: userId,
      createdAt: '',
      updatedAt: '',
    };
    await TaskService.saveTask(sub);
    setNewSubtaskTitle('');
    setShowAddSubtask(false);
    await onRefresh();
    toast('Subtask added');
  }

  async function toggleSubtaskDone(sub: Task) {
    const newStatus: TaskStatus = sub.status === 'done' ? 'todo' : 'done';
    await TaskService.saveTask({ ...sub, status: newStatus });
    await onRefresh();
  }

  async function handleAddLink() {
    if (!newLinkUrl.trim()) return;
    const link = await TaskService.saveTaskLink({
      id: '',
      taskId: task.id,
      url: newLinkUrl.trim(),
      label: newLinkLabel.trim() || undefined,
      createdAt: '',
    });
    setLinks((prev) => [...prev, link]);
    setNewLinkUrl('');
    setNewLinkLabel('');
    setShowAddLink(false);
    toast('Link added');
  }

  async function handleDeleteLink(id: string) {
    await TaskService.deleteTaskLink(id);
    setLinks((prev) => prev.filter((l) => l.id !== id));
    toast('Link removed');
  }

  async function handleAddAttachment() {
    const result = await TaskService.openFileDialog();
    if (!result) return;
    const att = await TaskService.saveTaskAttachment({
      id: '',
      taskId: task.id,
      fileName: result.fileName,
      mimeType: result.mimeType,
      data: result.data,
      createdAt: '',
    });
    setAttachments((prev) => [...prev, att]);
    toast('Attachment added');
  }

  async function handleDeleteAttachment(id: string) {
    await TaskService.deleteTaskAttachment(id);
    setAttachments((prev) => prev.filter((a) => a.id !== id));
    toast('Attachment removed');
  }

  function downloadAttachment(att: TaskAttachment) {
    const byteChars = atob(att.data);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
    const blob = new Blob([new Uint8Array(byteNumbers)], {
      type: att.mimeType || 'application/octet-stream',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = att.fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleDelete() {
    if (!confirm('Delete this task and all subtasks?')) return;
    await TaskService.deleteTask(task.id);
    toast('Task deleted');
    onClose();
    await onRefresh();
  }

  return (
    <div
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={overlayStyle}
    >
      <div
        style={{
          ...modalStyle,
          width: 640,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}
        >
          <div style={{ fontSize: 10, color: 'var(--tm)' }}>
            Created by {getMemberName(task.createdBy)} —{' '}
            {new Date(task.createdAt).toLocaleDateString()}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--tm)',
              cursor: 'pointer',
              padding: 6,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <X size={18} />
          </button>
        </div>

        <div
          style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          {/* Title */}
          <input
            value={editTitle}
            onChange={(e) => {
              setEditTitle(e.target.value);
              setDirty(true);
            }}
            aria-label="Task title"
            style={{
              ...inputStyle,
              fontSize: 16,
              fontWeight: 700,
              border: 'none',
              padding: '4px 0',
              background: 'transparent',
            }}
          />

          {/* Status + Priority + Due row */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ flex: 1 }}>
              <SearchableSelect
                value={editStatus}
                options={STATUS_COLS.map((s) => ({ value: s.id, label: s.label }))}
                onChange={(v) => {
                  setEditStatus(v as TaskStatus);
                  setDirty(true);
                }}
                clearable={false}
                searchPlaceholder="Search statuses…"
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <SearchableSelect
                value={editPriority}
                options={[
                  { value: 'low', label: 'Low' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'high', label: 'High' },
                  { value: 'urgent', label: 'Urgent' },
                ]}
                onChange={(v) => {
                  setEditPriority(v as TaskPriority);
                  setDirty(true);
                }}
                clearable={false}
                searchPlaceholder="Search…"
                style={inputStyle}
              />
            </div>
            <input
              type="date"
              value={editDueDate}
              onChange={(e) => {
                setEditDueDate(e.target.value);
                setDirty(true);
              }}
              style={{ ...inputStyle, flex: 1 }}
            />
          </div>

          {/* Projects (multi-select) */}
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--tm)',
                marginBottom: 6,
                textTransform: 'uppercase',
                letterSpacing: '.05em',
              }}
            >
              Projects
            </div>
            <SearchableMultiSelect
              options={projects
                .filter((p) => p.status === 'active')
                .map((p) => ({ value: p.id, label: p.name || 'Untitled', sub: p.projectNumber }))}
              selected={editProjectIds}
              onChange={(ids) => {
                setEditProjectIds(ids);
                setDirty(true);
              }}
              placeholder="Search projects..."
            />
          </div>

          {/* Description */}
          <RichTextEditor
            value={editDesc}
            onChange={(v) => {
              setEditDesc(v);
              setDirty(true);
            }}
            placeholder="Add a description..."
          />

          {/* Assignees */}
          {canAssign && (
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--tm)',
                  marginBottom: 6,
                  textTransform: 'uppercase',
                  letterSpacing: '.05em',
                }}
              >
                Assigned To
              </div>
              <SearchableMultiSelect
                options={members
                  .filter((m) => m.active)
                  .map((m) => ({ value: m.id, label: m.name }))}
                selected={taskAssigneeIds}
                onChange={(ids) => {
                  setTaskAssigneeIds(ids);
                  setDirty(true);
                }}
                placeholder="Search team members..."
              />
            </div>
          )}

          {/* Links */}
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 6,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--tm)',
                  textTransform: 'uppercase',
                  letterSpacing: '.05em',
                }}
              >
                Links {links.length > 0 ? `(${links.length})` : ''}
              </span>
              <button
                onClick={() => setShowAddLink(true)}
                style={{
                  fontSize: 10,
                  color: 'var(--ac)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                + Add
              </button>
            </div>
            {links.map((lnk) => (
              <div
                key={lnk.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '4px 0',
                  borderBottom: '1px solid var(--bd)',
                }}
              >
                <a
                  href={lnk.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  style={{
                    fontSize: 13,
                    color: 'var(--ac)',
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textDecoration: 'underline',
                  }}
                >
                  {lnk.label || lnk.url}
                </a>
                <button
                  onClick={() => handleDeleteLink(lnk.id)}
                  aria-label="Remove link"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--dg)',
                    cursor: 'pointer',
                    flexShrink: 0,
                    padding: 6,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <X size={13} />
                </button>
              </div>
            ))}
            {showAddLink && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
                <input
                  value={newLinkUrl}
                  onChange={(e) => setNewLinkUrl(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddLink()}
                  placeholder="https://..."
                  style={{ ...inputStyle, fontSize: 11, padding: '4px 8px' }}
                  autoFocus
                />
                <input
                  value={newLinkLabel}
                  onChange={(e) => setNewLinkLabel(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddLink()}
                  placeholder="Label (optional)"
                  style={{ ...inputStyle, fontSize: 11, padding: '4px 8px' }}
                />
                <div style={{ display: 'flex', gap: 6 }}>
                  <Button variant="secondary" size="sm" onClick={handleAddLink}>
                    Add
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label="Cancel"
                    onClick={() => {
                      setShowAddLink(false);
                      setNewLinkUrl('');
                      setNewLinkLabel('');
                    }}
                  >
                    <X size={14} />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Attachments */}
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 6,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--tm)',
                  textTransform: 'uppercase',
                  letterSpacing: '.05em',
                }}
              >
                Attachments {attachments.length > 0 ? `(${attachments.length})` : ''}
              </span>
              <button
                onClick={handleAddAttachment}
                style={{
                  fontSize: 10,
                  color: 'var(--ac)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                + Add
              </button>
            </div>
            {attachments.map((att) => (
              <div
                key={att.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '4px 0',
                  borderBottom: '1px solid var(--bd)',
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    color: 'var(--tx)',
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {att.fileName}
                </span>
                <button
                  onClick={() => downloadAttachment(att)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--ac)',
                    cursor: 'pointer',
                    fontSize: 10,
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  Download
                </button>
                <button
                  onClick={() => handleDeleteAttachment(att.id)}
                  aria-label="Remove attachment"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--dg)',
                    cursor: 'pointer',
                    flexShrink: 0,
                    padding: 6,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>

          {/* Subtasks */}
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 6,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--tm)',
                  textTransform: 'uppercase',
                  letterSpacing: '.05em',
                }}
              >
                Subtasks{' '}
                {subtasks.length > 0
                  ? `(${subtasks.filter((s) => s.status === 'done').length}/${subtasks.length})`
                  : ''}
              </span>
              <button
                onClick={() => setShowAddSubtask(true)}
                style={{
                  fontSize: 10,
                  color: 'var(--ac)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                + Add
              </button>
            </div>
            {subtasks.map((sub) => (
              <div
                key={sub.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '4px 0',
                  borderBottom: '1px solid var(--bd)',
                }}
              >
                <div
                  onClick={() => toggleSubtaskDone(sub)}
                  role="checkbox"
                  aria-checked={sub.status === 'done'}
                  aria-label={sub.title}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleSubtaskDone(sub);
                    }
                  }}
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 3,
                    border: `1.5px solid ${sub.status === 'done' ? 'var(--ok)' : 'var(--bd)'}`,
                    background: sub.status === 'done' ? 'var(--ok)' : 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-text-inverse)',
                    flexShrink: 0,
                  }}
                >
                  {sub.status === 'done' ? <Check size={11} /> : null}
                </div>
                <span
                  style={{
                    fontSize: 13,
                    color: sub.status === 'done' ? 'var(--tm)' : 'var(--tx)',
                    textDecoration: sub.status === 'done' ? 'line-through' : 'none',
                    flex: 1,
                  }}
                >
                  {sub.title}
                </span>
              </div>
            ))}
            {showAddSubtask && (
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <input
                  value={newSubtaskTitle}
                  onChange={(e) => setNewSubtaskTitle(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddSubtask()}
                  placeholder="Subtask title..."
                  style={{ ...inputStyle, flex: 1, fontSize: 11, padding: '4px 8px' }}
                  autoFocus
                />
                <Button variant="secondary" size="sm" onClick={handleAddSubtask}>
                  Add
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  aria-label="Cancel"
                  onClick={() => {
                    setShowAddSubtask(false);
                    setNewSubtaskTitle('');
                  }}
                >
                  <X size={14} />
                </Button>
              </div>
            )}
          </div>

          {/* Comments */}
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--tm)',
                marginBottom: 6,
                textTransform: 'uppercase',
                letterSpacing: '.05em',
              }}
            >
              Comments ({comments.length})
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                maxHeight: 200,
                overflow: 'auto',
              }}
            >
              {comments.map((c) => (
                <div
                  key={c.id}
                  style={{
                    padding: '8px 10px',
                    background: 'var(--sa)',
                    borderRadius: 6,
                    border: '1px solid var(--bd)',
                  }}
                >
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}
                  >
                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx)' }}>
                      {getMemberName(c.authorId)}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--tm)' }}>
                      {new Date(c.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--tx)', whiteSpace: 'pre-wrap' }}>
                    {c.text}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <input
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                placeholder="Write a comment..."
                style={{ ...inputStyle, flex: 1, fontSize: 13, padding: '6px 10px' }}
              />
              <Button variant="secondary" size="sm" onClick={handleAddComment}>
                Send
              </Button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 16,
            paddingTop: 12,
            borderTop: '1px solid var(--bd)',
          }}
        >
          <Button variant="destructive" size="sm" onClick={handleDelete}>
            Delete
          </Button>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
            {dirty && (
              <Button variant="default" size="sm" onClick={handleSave}>
                Save changes
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Styles ───

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'rgba(0,0,0,.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};
const modalStyle: React.CSSProperties = {
  background: 'var(--sf)',
  borderRadius: 10,
  padding: 24,
  border: '1px solid var(--bd)',
  boxShadow: 'var(--shadow-xl)',
  overflow: 'auto',
};
const inputStyle: React.CSSProperties = {
  padding: '7px 10px',
  borderRadius: 5,
  border: '1px solid var(--bd)',
  background: 'var(--ib)',
  color: 'var(--tx)',
  fontSize: 13,
  width: '100%',
};
const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: 12,
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--tm)',
};

(window as any).TasksPage = TasksPage;
export { TasksPage };
